import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppState } from '../context/AppStateContext';
import { SupportAccessService, SupportGrant, formatSupportExpiry } from '../services/supportDataService';

/** Re-check this often, so a request or an expiry shows without a reload. */
const POLL_MS = 5 * 60 * 1000;

/**
 * Header pill for Admins: "Support request waiting" or "Support access until …"
 * (tdp-api docs/support-access-plan.md §5). Clicking opens Organisation
 * settings, where the Support Access section is. Renders nothing for
 * non-Admins or when there is no pending or active grant.
 */
const SupportAccessIndicator: React.FC = () => {
  const { userRoleIds, roles, selectedOrganisation, activeUser } = useAppState();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState<SupportGrant | null>(null);
  // Not in a support session: support acts with Admin roles, but support
  // access is the organisation's to manage, and the API refuses (and logs)
  // every call support makes to it.
  const isAdmin = roles.Admin !== undefined && userRoleIds.includes(roles.Admin) && !activeUser?.isSupport;
  const orgId = selectedOrganisation?.id;

  useEffect(() => {
    if (!isAdmin || !orgId) { setOpen(null); return; }
    let cancelled = false;
    const check = () => {
      SupportAccessService.fetchStatus()
        .then(g => { if (!cancelled) setOpen(g); })
        .catch(() => { /* a missed check is not worth interrupting anyone over */ });
    };
    check();
    const timer = window.setInterval(check, POLL_MS);
    return () => { cancelled = true; window.clearInterval(timer); };
    // Re-checked on navigation too, so approving on the Admin page updates the
    // pill as soon as the admin moves on.
  }, [isAdmin, orgId, location.pathname]);

  if (!open) return null;

  const pending = open.state === 'pending';
  return (
    <button
      type="button"
      className={`support-indicator${pending ? ' support-indicator--pending' : ''}`}
      onClick={() => navigate('/admin?tab=organisation&section=support')}
      title="Open Organisation settings → Support Access"
    >
      <i className={`fas ${pending ? 'fa-bell' : 'fa-life-ring'}`} aria-hidden="true" />
      {pending ? 'Support request waiting' : `Support access until ${formatSupportExpiry(open.expiresAt ?? '')}`}
    </button>
  );
};

export default SupportAccessIndicator;
